import React, { createContext, useContext, useState, useEffect } from 'react';

/**
 * OverrideModeContext — global Super Admin "Override Mode" toggle.
 *
 * When ON:
 *   - SuperAdminDashboard shows a sticky red banner "OVERRIDE MODE ACTIVE".
 *   - Quote view pages expose extra admin actions (force-status,
 *     reassign, edit-any-field) gated by `useOverrideMode().active`.
 *   - Every action POSTs to /api/admin/quotes/{id}/{override|force-status|
 *     reassign} and is audit-logged server-side in db.admin_overrides.
 *
 * Persisted in sessionStorage (intentionally NOT localStorage) so the
 * mode auto-clears when the admin closes the tab — reduces "left it on
 * by accident" risk.
 */
const OverrideModeContext = createContext({
  active: false,
  toggle: () => {},
  setActive: () => {},
});

export const OverrideModeProvider = ({ children }) => {
  const [active, setActive] = useState(() => {
    try {
      return window.sessionStorage.getItem('zion_override_mode') === 'on';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      if (active) window.sessionStorage.setItem('zion_override_mode', 'on');
      else window.sessionStorage.removeItem('zion_override_mode');
    } catch {
      /* ignore */
    }
  }, [active]);

  const toggle = () => setActive((a) => !a);

  return (
    <OverrideModeContext.Provider value={{ active, toggle, setActive }}>
      {children}
    </OverrideModeContext.Provider>
  );
};

export const useOverrideMode = () => useContext(OverrideModeContext);
